import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ShieldAlert, X, ArrowRight, Users, Wallet, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { formatUSD } from "@/lib/utils";
import PasswordInput from "@/components/ui/PasswordInput";

const inputCls =
  "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]";

const ROLE_LABELS = {
  AGENT: "Agent",
  MASTER_AGENT: "Master Agent",
  MASTER_ADMIN: "Master Admin",
};

export default function ChangeRoleModal({ user, onClose, onDone }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [newRole, setNewRole] = useState("");
  const [newParentId, setNewParentId] = useState("");
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [downgradeAction, setDowngradeAction] = useState("");
  const [downgradeTarget, setDowngradeTarget] = useState("");

  // Refs keep the effect stable against parent re-renders (which would
  // otherwise recreate `onClose`/`t` on every keystroke in the parent search
  // input and cause the role-preview fetch to fire again).
  const onCloseRef = useRef(onClose);
  const tRef = useRef(t);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/role-preview`);
        if (!live) return;
        setPreview(data);
        setNewRole(data.user.role);
        setNewParentId(data.current_parent?.id || data.admin_user?.id || "");
      } catch (e) {
        toast.error(e.response?.data?.detail || tRef.current("change_role.failed_preview"));
        onCloseRef.current();
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [user.id]);

  const roleOptions = useMemo(() => {
    // MASTER_ADMIN is intentionally NOT selectable — it's a platform-owner
    // singleton role and cannot be granted via Change Role.
    return ["AGENT", "MASTER_AGENT"];
  }, []);

  const parentOptions = useMemo(() => {
    if (!preview) return [];
    const opts = [];
    if (preview.admin_user) {
      opts.push({
        id: preview.admin_user.id,
        label: `${t("change_role.parent_admin")} · ${preview.admin_user.username}`,
        role: "MASTER_ADMIN",
      });
    }
    (preview.available_masters || []).forEach((m) => {
      if (m.id !== user.id) {
        opts.push({
          id: m.id,
          label: `${t("role.MASTER_AGENT")} · ${m.username}${m.name ? ` (${m.name})` : ""}`,
          role: "MASTER_AGENT",
        });
      }
    });
    return opts;
  }, [preview, t, user.id]);

  const showParentPicker = newRole === "AGENT" || newRole === "MASTER_AGENT";
  // AGENT: any parent (admin or master). MASTER_AGENT: parent must be MASTER_ADMIN.
  const filteredParentOptions = useMemo(() => {
    if (newRole === "MASTER_AGENT") return parentOptions.filter((p) => p.role === "MASTER_ADMIN");
    return parentOptions;
  }, [parentOptions, newRole]);

  const isDowngradeWithChildren =
    preview?.user.role === "MASTER_AGENT" &&
    newRole !== "MASTER_AGENT" &&
    (preview?.sub_agents_count || 0) > 0;

  const currentParent = preview?.current_parent;
  const newParentLabel =
    filteredParentOptions.find((p) => p.id === newParentId)?.label || "—";

  const submit = async () => {
    if (!password.trim()) { toast.error(t("change_role.password_required")); return; }
    if (showParentPicker && !newParentId) { toast.error(t("change_role.pick_parent")); return; }
    if (isDowngradeWithChildren) {
      if (!downgradeAction) { toast.error(t("change_role.pick_downgrade")); return; }
      if (downgradeAction === "MOVE_TO_MASTER" && !downgradeTarget) {
        toast.error(t("change_role.pick_downgrade_target")); return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/change-role`, {
        new_role: newRole,
        new_parent_id: newParentId,
        password,
        reason: reason.trim() || null,
        downgrade_action: isDowngradeWithChildren ? downgradeAction : null,
        downgrade_target_master_id: downgradeAction === "MOVE_TO_MASTER" ? downgradeTarget : null,
      });
      toast.success(t("change_role.saved"));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("change_role.failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div
        data-testid="change-role-modal"
        className="relative bg-white rounded-xl shadow-xl w-full md:max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] flex items-center gap-1.5">
              <ShieldAlert className="w-3 h-3" /> {t("change_role.section_label")}
            </div>
            <div className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t("change_role.title", { username: user.username })}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="change-role-close"
            className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("change_role.loading")}
          </div>
        ) : preview ? (
          <div className="p-6 space-y-5">
            {/* Current snapshot */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoCard
                title={t("change_role.current_role")}
                value={t(`role.${preview.user.role}`)}
                tone="slate"
              />
              <InfoCard
                title={t("change_role.current_parent")}
                value={currentParent ? `${currentParent.username}${currentParent.name ? ` · ${currentParent.name}` : ""}` : t("change_role.parent_none_admin")}
                tone="slate"
              />
              <InfoCard
                title={t("change_role.wallet_balance")}
                value={formatUSD(preview.wallet_balance_usd)}
                tone="emerald"
                icon={Wallet}
              />
              <InfoCard
                title={t("change_role.history_count")}
                value={`${preview.topups_count} · ${preview.commissions_count}`}
                extra={t("change_role.history_hint")}
                tone="violet"
                icon={ScrollText}
              />
            </div>

            {/* Role picker */}
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                {t("change_role.new_role")}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {roleOptions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    data-testid={`change-role-opt-${r}`}
                    onClick={() => setNewRole(r)}
                    className={`h-10 rounded-md border text-xs font-semibold transition-colors ${
                      newRole === r
                        ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {t(`role.${r}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Parent picker */}
            {showParentPicker && (
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                  {newRole === "MASTER_AGENT" ? t("change_role.new_parent_admin") : t("change_role.new_parent_master")}
                </label>
                <select
                  data-testid="change-role-parent-select"
                  value={newParentId}
                  onChange={(e) => setNewParentId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">{t("change_role.pick_parent")}</option>
                  {filteredParentOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Impact preview */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-900">
                {t("change_role.impact_title")}
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-900">
                <span className="font-mono text-xs px-2 py-1 rounded bg-white border border-slate-200">
                  {t(`role.${preview.user.role}`)}
                </span>
                <ArrowRight className="w-4 h-4 text-blue-600" />
                <span className="font-mono text-xs px-2 py-1 rounded bg-white border border-blue-300 font-bold">
                  {t(`role.${newRole}`)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-700">
                <span className="opacity-70">{t("change_role.parent_label")}:</span>
                <span className="font-mono">
                  {currentParent?.username || t("change_role.parent_none_admin")}
                </span>
                <ArrowRight className="w-3 h-3 text-blue-600" />
                <span className="font-mono font-bold">{newParentLabel}</span>
              </div>
              <ul className="mt-3 space-y-1 text-[11px] text-blue-900">
                <li>• {t("change_role.impact_history")}</li>
                <li>• {t("change_role.impact_wallet")}</li>
                <li>• {t("change_role.impact_permissions")}</li>
                {preview.user.role === "MASTER_AGENT" && newRole === "AGENT" && (
                  <li>• {t("change_role.impact_master_perms_removed")}</li>
                )}
                {preview.user.role === "AGENT" && newRole === "MASTER_AGENT" && (
                  <li>• {t("change_role.impact_master_perms_granted")}</li>
                )}
              </ul>
            </div>

            {/* Downgrade sub-agents */}
            {isDowngradeWithChildren && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3" data-testid="change-role-downgrade">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-900">
                  <Users className="w-3 h-3" />
                  {t("change_role.downgrade_title", { count: preview.sub_agents_count })}
                </div>
                <div className="text-[11px] text-amber-800 mt-1 mb-3">
                  {t("change_role.downgrade_hint")}
                </div>
                <div className="space-y-2">
                  {[
                    { v: "MOVE_TO_ADMIN", label: t("change_role.dg_move_admin") },
                    { v: "MOVE_TO_MASTER", label: t("change_role.dg_move_master") },
                    { v: "BLOCK", label: t("change_role.dg_block") },
                  ].map((o) => (
                    <label
                      key={o.v}
                      className={`flex items-start gap-2 cursor-pointer p-2 rounded border ${
                        downgradeAction === o.v ? "bg-white border-amber-500" : "bg-white/50 border-transparent hover:bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="dg"
                        value={o.v}
                        checked={downgradeAction === o.v}
                        onChange={(e) => setDowngradeAction(e.target.value)}
                        className="mt-0.5"
                        data-testid={`change-role-dg-${o.v}`}
                      />
                      <span className="text-xs text-slate-800 font-medium">{o.label}</span>
                    </label>
                  ))}
                </div>
                {downgradeAction === "MOVE_TO_MASTER" && (
                  <select
                    data-testid="change-role-dg-target"
                    value={downgradeTarget}
                    onChange={(e) => setDowngradeTarget(e.target.value)}
                    className={`${inputCls} mt-2`}
                  >
                    <option value="">{t("change_role.pick_downgrade_target")}</option>
                    {(preview.available_masters || [])
                      .filter((m) => m.id !== user.id)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.username}{m.name ? ` · ${m.name}` : ""}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            )}

            {/* Reason + password */}
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                {t("change_role.reason")}
              </label>
              <textarea
                data-testid="change-role-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={300}
                placeholder={t("change_role.reason_placeholder")}
                className="flex w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                {t("change_role.confirm_password")} <span className="text-rose-500">*</span>
              </label>
              <PasswordInput
                data-testid="change-role-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("change_role.password_placeholder")}
                className={inputCls}
              />
            </div>
          </div>
        ) : null}

        {!loading && preview && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="change-role-submit"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("change_role.apply")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoCard({ title, value, extra, tone = "slate", icon: Icon }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    violet: "bg-violet-50 border-violet-200 text-violet-900",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70 flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {title}
      </div>
      <div className="text-base font-bold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value}
      </div>
      {extra ? <div className="text-[10px] opacity-70 mt-0.5">{extra}</div> : null}
    </div>
  );
}
